import { CaretRightIcon } from '@persona-id/design-tokens/icons';
import { DateTime } from 'luxon';
import { useCallback, useMemo, useState, VFC } from 'react';
import styled from 'styled-components';

import ConnectedEventTimeline from 'dashboard/components/ConnectedEventTimeline';
import FeatureFlagged from 'dashboard/components/FeatureFlagged';
import FieldsTable from 'dashboard/components/FieldsTable';
import ListMatches from 'dashboard/components/ListMatches';
import PersonaErrorBoundary from 'dashboard/components/PersonaErrorBoundary';
import RelationsTable from 'dashboard/components/RelationsTable';
import TagsSummary from 'dashboard/components/TagsSummary';
import { FormUpdateTagsData } from 'dashboard/forms/FormUpdateTags';
import { ColumnContainer, MainColumn, RightColumn } from 'dashboard/layouts/ShowViewLayoutV2';
import { Environment } from 'dashboard/models/environment';
import { RedactedObjectPath } from 'dashboard/models/event';
import { List } from 'dashboard/models/list';
import { ListItem } from 'dashboard/models/list-item';
import { ListMatch } from 'dashboard/models/list-match';
import { TransactionWithRelatedData } from 'dashboard/routes/DashboardTransactionsShowRoute/util';
import QuickLinks from 'dashboard/views/DashboardTransactionsShowView/TransactionDetail/QuickLinks';
import Card from 'design-system/components/Card';
import { TransitionDuration } from 'design-system/variables/transitions';
import { TIMEZONE_NAME_ABBREVIATED } from 'lib/constants/luxonFormats';
import { FieldsMap, fillRedacted } from 'lib/fields';
import { ApiModel } from 'lib/interfaces/api';
import { captureMessage } from 'lib/reporting';

const WithClickable = styled.div.attrs(() => ({
  'aria-label': 'button',
}))<{ opened: boolean }>`
  cursor: pointer;

  svg {
    transition: transform ${TransitionDuration.Fast};
    transform: ${({ opened }) => (opened ? 'rotate(90deg)' : 'rotate(0deg)')};
  }
`;

const VisbilityToggle = styled.div<{ visible: boolean }>`
  display: ${({ visible }) => (visible ? 'block' : 'none')};
`;

type CollapsibleCardName = 'fields' | 'events' | 'list-matches';

interface OverviewProps {
  currentEnvironment: Environment;
  redactedObjectPaths: RedactedObjectPath[];
  relatedAccounts: TransactionWithRelatedData['relatedAccounts'];
  relatedCases: TransactionWithRelatedData['relatedCases'];
  relatedInquiries: TransactionWithRelatedData['relatedInquiries'];
  relatedReports: TransactionWithRelatedData['relatedReports'];
  relatedTransactions: TransactionWithRelatedData['relatedTransactions'];
  relatedVerifications: TransactionWithRelatedData['relatedVerifications'];
  relations: TransactionWithRelatedData['relations'];
  transaction: NonNullable<TransactionWithRelatedData['transaction']>;
  transactionTags: TransactionWithRelatedData['tags'];
  lists: Record<string, List>;
  listItems: Record<string, ListItem>;
  listMatches: Record<string, ListMatch>;
  handleUpdateTags: (tags: FormUpdateTagsData) => void;
}

const Overview: VFC<OverviewProps> = ({
  currentEnvironment,
  redactedObjectPaths,
  relatedAccounts,
  relatedCases,
  relatedInquiries,
  relatedReports,
  relatedTransactions,
  relatedVerifications,
  relations,
  transaction,
  transactionTags,
  lists,
  listItems,
  listMatches,
  handleUpdateTags,
}) => {
  const showQuickLinks =
    [
      ...relatedAccounts,
      ...relatedCases,
      ...relatedInquiries,
      ...relatedReports,
      ...relatedTransactions,
      ...relatedVerifications,
    ].length > 0;

  const fields: FieldsMap = useMemo(
    () => fillRedacted(transaction.attributes.fields, redactedObjectPaths),
    [redactedObjectPaths, transaction.attributes.fields],
  );

  const [collapsedCards, setCollapsedCards] = useState<CollapsibleCardName[]>([
    'fields',
    'events',
    'list-matches',
  ]);
  const toggleCardCollapsed = useCallback((cardName: CollapsibleCardName) => {
    setCollapsedCards((collapsed) => {
      return collapsed.includes(cardName)
        ? collapsed.filter((c) => c !== cardName)
        : [...collapsed, cardName];
    });
  }, []);

  return (
    <ColumnContainer>
      <MainColumn>
        <Card>
          <Card.Body>
            <WithClickable
              id='overview__card-title-fields'
              opened={collapsedCards.includes('fields')}
              onClick={() => toggleCardCollapsed('fields')}
            >
              <Card.Title icon={<CaretRightIcon />}>Fields</Card.Title>
            </WithClickable>
            <VisbilityToggle visible={collapsedCards.includes('fields')}>
              <FieldsTable
                fields={fields}
                allRedacted={transaction.attributes['redacted-at'] != null}
              />
            </VisbilityToggle>
          </Card.Body>
        </Card>
        {Object.keys(relations).length > 0 && (
          <Card>
            <Card.Body>
              <Card.Title>Relations</Card.Title>
              <RelationsTable relations={relations} />
            </Card.Body>
          </Card>
        )}
        <Card>
          <Card.Body>
            <WithClickable
              id='overview__card-title-events'
              opened={collapsedCards.includes('events')}
              onClick={() => toggleCardCollapsed('events')}
            >
              <Card.Title icon={<CaretRightIcon />}>
                Event timeline ({DateTime.local().toFormat(TIMEZONE_NAME_ABBREVIATED)})
              </Card.Title>
            </WithClickable>
            <VisbilityToggle visible={collapsedCards.includes('events')}>
              <PersonaErrorBoundary
                onCatch={(error) =>
                  captureMessage('Event timeline failed to load', 'error', {
                    error,
                    object: transaction.id,
                    component: 'DashboardTransactionsShowView',
                  })
                }
              >
                <ConnectedEventTimeline
                  currentEnvironment={currentEnvironment}
                  location='transaction'
                  objects={[transaction]}
                />
              </PersonaErrorBoundary>
            </VisbilityToggle>
          </Card.Body>
        </Card>
        <FeatureFlagged featureFlag='dashboard:decouple-list-matches-from-inquiries'>
          <Card>
            <Card.Body>
              <WithClickable
                id='overview__card-title-list-matches'
                opened={collapsedCards.includes('list-matches')}
                onClick={() => toggleCardCollapsed('list-matches')}
              >
                <Card.Title icon={<CaretRightIcon />}>List matches</Card.Title>
              </WithClickable>
              <VisbilityToggle visible={collapsedCards.includes('list-matches')}>
                <ListMatches lists={lists} listItems={listItems} listMatches={listMatches} />
              </VisbilityToggle>
            </Card.Body>
          </Card>
        </FeatureFlagged>
      </MainColumn>
      <RightColumn style={{ paddingRight: 0 }}>
        <TagsSummary
          tags={transactionTags}
          tagScope={ApiModel.Transaction}
          handleUpdateTags={handleUpdateTags}
        />
        {showQuickLinks && (
          <QuickLinks
            relatedAccounts={relatedAccounts}
            relatedCases={relatedCases}
            relatedInquiries={relatedInquiries}
            relatedReports={relatedReports}
            relatedTransactions={relatedTransactions}
            relatedVerifications={relatedVerifications}
          />
        )}
      </RightColumn>
    </ColumnContainer>
  );
};

export default Overview;
